import { Store, useAppDispatch } from 'src/redux/Store';
import { useSelector } from 'react-redux';
import { normalizeInvoiceForUser } from 'src/lib/InvoiceHelper';
import { makeStyles } from '@material-ui/core/styles';
import { t } from 'src/lib/language';
import { useMediaQuery } from 'react-responsive';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { setSelections } from 'src/redux/actions/selectionActions';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import { useEffect } from 'react';
import WarningIcon from '@material-ui/icons/Warning';
import { INVOICE_PAYMENT_STATUS, INVOICE_PAYMENT_STATUS_COLOR } from 'src/constants/Invoices';
import { SEARCH_LOGICAL_OPERATORS } from 'src/models/AdvanceSearchModel';
import { getEntitiesAction, setFilters } from 'src/redux/actions/querybuilderActions';
import { InvoiceSections } from 'src/components/Pages/UserInvoicesPage';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';
import { getInvoiceFilter } from 'src/lib/PaymentHelper';

const useStyles = (isTabletOrMobile:boolean) => makeStyles((theme) => ({
  card: {
    border: '1px solid rgba(0, 0, 0, 0.25)',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: '5px',
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
      cursor: 'pointer',
    },
    flex: 1,
    width: '100%',
    maxWidth: '680px',
  },
  label: {
    fontWeight: 'bold',
    marginRight: '8px',
  },
  selected: {
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: lighten(theme.palette.primary.main, 0.9),
  },
  list: {
    marginTop: '0px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  mobile: {
    marginTop: '48px',
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '18px',
  },
  submit: {
    marginLeft: 'auto',
    display: 'flex',
    marginBottom: '8px',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
    gap: '8px',
    alignItems: 'center',
  },
  tabs: {
    width: '100%',
    height: '26px',
    display: 'flex',
    maxWidth: '680px',
    backgroundColor: 'white',
  },
  tab: {
    border: '1px solid #eee',
    borderTop: 'none',
    height: '26px',
    flex: 1,
    padding: '0 8px',
    '&:hover': {
      opacity: 0.5,
      cursor: 'pointer',
    },
  },
  tabSelected: {
    backgroundColor: theme.palette.primary.main,
    color: '#FFF',
  },
  col2: {
    flex: 1,
    flexDirection: 'column',
    flexBasis: isTabletOrMobile ? '100%' : '50%',
    width: '100%',
    alignItems: 'center',
  },
  col3: {
    flex: 1,
    flexDirection: 'column',
    flexBasis: isTabletOrMobile ? '100%' : '33%',
    width: '100%',
    alignItems: 'center',
  },
  col4: {
    flex: 1,
    flexDirection: 'column',
    flexBasis: isTabletOrMobile ? '100%' : '25%',
    width: '100%',
    alignItems: 'center',
  },
}));

interface Props {
  section: InvoiceSections,
}

const ManageUserInvoices = (props: Props) => {
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });
  const classes = useStyles(isTabletOrMobile)();
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const { section } = props;

  useEffect(() => {
    const start = moment().subtract(2, 'year');
    const end = moment();

    const filters = getInvoiceFilter(
      start,
      end,
    );
    dispatch(setFilters({
      entity: ENTITIES.invoices,
      filters: { [SEARCH_LOGICAL_OPERATORS.and]: filters },
    }));
    dispatch(getEntitiesAction(ENTITIES.invoices));
  }, [section]);

  const formatter = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'CAD',
  });

  const selections = useSelector((state: Store) => {
    if (!!state.selected[ENTITIES.invoices]) {
      return [...state.selected[ENTITIES.invoices]];
    }
    return [];
  });

  const invoices = useSelector((state: Store) => state.querybuilder.invoices?.result?.list || []);

  const handleOnClick = (id?: number) => {
    if (!id) return;
    const index = selections.indexOf(id);

    if (index >= 0) {
      selections.splice(index, 1);
    } else {
      selections.push(id);
    }

    dispatch(setSelections({
      entity: ENTITIES.invoices,
      selections,
    }));
  };

  const getClasses = (id?: number) => {
    if (selections.indexOf(id) < 0) {
      return classes.card;
    }
    return `${classes.card} ${classes.selected}`;
  };

  const showItems = () => invoices.map((item) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { due_date, issued_date, ...data } = item;
    const invoice = normalizeInvoiceForUser(data);
    invoice.issued_date = moment(invoice.issued_date);
    invoice.due_date = moment(invoice.due_date);
    if (
      section === InvoiceSections.overdue
    ) {
      if (
        invoice.due_date.isAfter(moment())) {
        return undefined;
      }
      if (
        invoice.payment_status === INVOICE_PAYMENT_STATUS.paid ||
        invoice.payment_status === INVOICE_PAYMENT_STATUS.scheduled
      ) {
        return undefined;
      }
    }
    if (section === InvoiceSections.paid) {
      if (invoice.payment_status !== INVOICE_PAYMENT_STATUS.paid) {
        return undefined;
      }
    }
    const isDue = () => {
      switch (invoice.payment_status) {
        case INVOICE_PAYMENT_STATUS.paid:
        case INVOICE_PAYMENT_STATUS.scheduled:
          return false;
        default:
          if (!moment.isMoment(invoice.due_date)) return false;
          return invoice.due_date.isBefore(moment());
      }
    };
    const hasWarning = () => {
      switch (invoice.payment_status) {
        case INVOICE_PAYMENT_STATUS.scheduled_due:
          return true;
        default:
          return false;
      }
    };
    const getStatusColor = () => {
      const color = INVOICE_PAYMENT_STATUS_COLOR[
        invoice.payment_status ?? INVOICE_PAYMENT_STATUS_COLOR.not_paid
      ];
      return hasWarning() ? '#d41d1d' : color;
    };

    const getDate = (value: any) => {
      if (!moment.isMoment(value)) return '';
      return value.format('YYYY-MM-DD');
    };

    return (
      <div
        className={getClasses(invoice.id)}
        onClick={() => handleOnClick(invoice.id)}
        key={invoice.invoice_number}
      >
        <div className={classes.row}>
          <span><b>{ invoice.title }</b></span>
          { (hasWarning() || isDue()) && (
            <WarningIcon style={{
              color: '#d41d1d',
              marginRight: 0,
              marginLeft: 'auto',
            }}
            />
          )}
        </div>
        <div>{invoice.id}</div>
        <div style={{ color: isDue() ? '#d41d1d' : undefined }}>
          Due date: <span>{ getDate(invoice.due_date) }</span>
        </div>
        <div>
          Amount due: <span>{formatter.format(invoice.total ?? 0)}</span>
        </div>
        <div>
          Amount paid: <span>{formatter.format(invoice.paid ?? 0)}</span>
        </div>
        <div className={classes.row}>
          <span
            style={{
              color: getStatusColor(),
            }}
          >
            {t(lang, `forms.invoices.${invoice.payment_status}`)}
          </span>
        </div>
      </div>
    );
  });

  return (
    <>
      <div className={`${isTabletOrMobile ? classes.mobile : classes.list}`}>
        {showItems()}
      </div>
    </>
  );
};

export default ManageUserInvoices;
//
